import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { agendaApi } from '../../api/endpoints.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useToast } from '../../hooks/useToast.jsx';
import { useApiError } from '../../hooks/useApiError.js';
import PageHeader from '../../components/PageHeader.jsx';
import Toolbar from '../../components/Toolbar.jsx';
import Card from '../../components/Card.jsx';
import Badge from '../../components/Badge.jsx';
import Spinner from '../../components/Spinner.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import Modal from '../../components/Modal.jsx';
import MonthCalendar, { MonthNav, firstOfMonth } from '../../components/MonthCalendar.jsx';
import { Select } from '../../components/Field.jsx';
import { formatDayLong, formatTime, isoDay, fullName } from '../../utils/format.js';
import { APPOINTMENT_STATUSES, PERSON_TONES as TONES } from '../../constants.js';


/**
 * Team agenda, in two shapes:
 *   calendar - the month at a glance, one chip per RDV
 *   list     - the same data grouped by day, easier to scan in detail
 *
 * Who sees what: teleprospection and agency managers hold AGENDA_READ_ALL and get
 * every sales diary of their agency; a sales person receives only their own RDV,
 * so for them the scope switch, the person filter and the legend are hidden.
 */
const CalendarPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const toMessage = useApiError();

  const [view, setView] = useState('calendar');
  // Sales only ever receives its own RDV from the API, so the scope switch
  // and the per-person filter are meaningless for that role.
  const [scope, setScope] = useState('all'); // all | mine
  const [cursor, setCursor] = useState(firstOfMonth());
  const [commercialId, setCommercialId] = useState('');
  const [detail, setDetail] = useState(null);

  const year = cursor.getUTCFullYear();
  const month = cursor.getUTCMonth();
  const from = new Date(Date.UTC(year, month, 1)).toISOString();
  const to = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59)).toISOString();

  const { data: commercials } = useApi(() => agendaApi.commercials(), []);
  const { data, loading, refresh } = useApi(
    () => agendaApi.appointments({ from, to, commercialId: commercialId || undefined }),
    [from, to, commercialId],
  );

  const toneFor = useMemo(() => {
    const order = (commercials?.items ?? []).map((item) => item.id);
    return (id) => TONES[Math.max(0, order.indexOf(id)) % TONES.length];
  }, [commercials]);

  /** `scope` is applied client-side: the API already sent the whole team. */
  const visible = useMemo(() => {
    const rows = data?.items ?? [];
    return scope === 'mine' ? rows.filter((row) => row.commercialId === user.id) : rows;
  }, [data, scope, user.id]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const appointment of visible) {
      const key = appointment.startAt.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(appointment);
    }
    for (const list of map.values()) list.sort((a, b) => (a.startAt < b.startAt ? -1 : 1));
    return map;
  }, [visible]);

  const seesTeam = user.permissions?.includes('agenda:read:all') ?? false;
  const mineCount = (data?.items ?? []).filter((row) => row.commercialId === user.id).length;
  const teamCount = (data?.items ?? []).length;

  const cancel = async (appointment) => {
    try {
      await agendaApi.cancel(appointment.id, '');
      toast.success(t('agenda.status.CANCELLED'));
      setDetail(null);
      refresh();
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const changeStatus = async (appointment, status) => {
    try {
      await agendaApi.update(appointment.id, { status });
      toast.success(t(`agenda.status.${status}`));
      refresh();
      setDetail((current) => (current ? { ...current, status } : current));
    } catch (err) {
      toast.error(toMessage(err));
    }
  };

  const renderDay = (key) => {
    const items = byDay.get(key);
    if (!items?.length) return null;

    return items.map((appointment) => (
      <button
        key={appointment.id}
        type="button"
        className={[
          'rdv-chip',
          `rdv-chip--${toneFor(appointment.commercialId)}`,
          appointment.commercialId === user.id ? 'rdv-chip--mine' : '',
          appointment.status === 'CANCELLED' ? 'rdv-chip--cancelled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => setDetail(appointment)}
        title={`${formatTime(appointment.startAt, i18n.resolvedLanguage)} ${appointment.title} - ${fullName(appointment.commercial)}`}
      >
        <span className="rdv-chip__time">{formatTime(appointment.startAt, i18n.resolvedLanguage)}</span>
        <span className="rdv-chip__label">{appointment.customerName || appointment.title}</span>
        <span className="rdv-chip__who">{appointment.commercial?.firstName}</span>
      </button>
    ));
  };

  return (
    <>
      <PageHeader
        title={seesTeam ? t('agenda.teamCalendar') : t('agenda.myCalendar')}
        subtitle={
          seesTeam
            ? t('agenda.teamCount', { team: teamCount, mine: mineCount })
            : t('agenda.mineOnly', { count: mineCount })
        }
      />

      <Toolbar>
        <MonthNav cursor={cursor} onChange={setCursor} />

        {/* Two ways to read the same data */}
        <div className="segmented" role="group" aria-label={t('agenda.view')}>
          <button
            type="button"
            className={view === 'calendar' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setView('calendar')}
          >
            {t('agenda.viewCalendar')}
          </button>
          <button
            type="button"
            className={view === 'list' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setView('list')}
          >
            {t('agenda.viewList')}
          </button>
        </div>

        {/* Team vs mine - only meaningful when you can see more than your own */}
        {seesTeam ? (
        <div className="segmented" role="group" aria-label={t('agenda.scope')}>
          <button
            type="button"
            className={scope === 'all' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setScope('all')}
          >
            {t('agenda.scopeAll')} ({teamCount})
          </button>
          <button
            type="button"
            className={scope === 'mine' ? 'segmented__btn segmented__btn--on' : 'segmented__btn'}
            onClick={() => setScope('mine')}
          >
            {t('agenda.scopeMine')} ({mineCount})
          </button>
        </div>
        ) : null}

        {seesTeam ? (
          <Select
            value={commercialId}
            onChange={setCommercialId}
            placeholder={t('agenda.allCommercials')}
            options={(commercials?.items ?? []).map((item) => ({ value: item.id, label: fullName(item) }))}
          />
        ) : null}
      </Toolbar>

      {/* Who is who, so a colour in the grid means something */}
      {seesTeam && commercials?.items?.length ? (
        <div className="legend">
          {commercials.items.map((item) => (
            <span key={item.id} className={`legend__item legend__item--${toneFor(item.id)}`}>
              <span className="legend__dot" aria-hidden="true" />
              {fullName(item)}
              {item.id === user.id ? ` (${t('agenda.scopeMine')})` : ''}
            </span>
          ))}
        </div>
      ) : null}

      {loading ? <Spinner /> : null}

      {!loading && view === 'calendar' ? <MonthCalendar cursor={cursor} renderDay={renderDay} /> : null}

      {!loading && view === 'list' ? (
        <>
          {[...byDay.entries()].sort().map(([day, appointments]) => (
            <Card key={day} title={formatDayLong(day, i18n.resolvedLanguage)}>
              <ul className="agenda-list">
                {appointments.map((appointment) => (
                  <li key={appointment.id}>
                    <button
                      type="button"
                      className={`agenda-item agenda-item--${appointment.status.toLowerCase()}`}
                      onClick={() => setDetail(appointment)}
                    >
                      <span className="agenda-item__time">
                        {formatTime(appointment.startAt, i18n.resolvedLanguage)}
                        <span className="agenda-item__time-end">
                          {formatTime(appointment.endAt, i18n.resolvedLanguage)}
                        </span>
                      </span>
                      <span className="agenda-item__body">
                        <span className="agenda-item__title">{appointment.title}</span>
                        <span className="agenda-item__meta">
                          {appointment.customerName ? `${appointment.customerName} - ` : ''}
                          {t('agenda.with')} {fullName(appointment.commercial)}
                          {appointment.bookedBy ? ` - ${t('agenda.bookedBy')} ${fullName(appointment.bookedBy)}` : ''}
                        </span>
                      </span>
                      <span className="agenda-item__tags">
                        {appointment.commercialId === user.id ? (
                          <Badge value="MINE" tone="green" label={t('agenda.scopeMine')} />
                        ) : null}
                        <Badge value={appointment.channel} label={t(`agenda.channels.${appointment.channel}`)} />
                        <Badge value={appointment.status} label={t(`agenda.status.${appointment.status}`)} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
          {!byDay.size ? <EmptyState title={t('agenda.noAppointments')} /> : null}
        </>
      ) : null}

      {!loading && view === 'calendar' && !byDay.size ? (
        <p className="muted empty-month">{t('agenda.noAppointments')}</p>
      ) : null}

      <Modal
        open={Boolean(detail)}
        title={detail?.title ?? ''}
        onClose={() => setDetail(null)}
        footer={
          detail ? (
            <>
              {detail.customerId ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setDetail(null);
                    navigate(`/customers/${detail.customerId}`);
                  }}
                >
                  {t('agenda.openCustomer')}
                </button>
              ) : null}
              {detail.leadId ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setDetail(null);
                    navigate(`/leads/${detail.leadId}`);
                  }}
                >
                  {t('agenda.openBrief')}
                </button>
              ) : null}
              {detail.status !== 'CANCELLED' ? (
                <>
                  <button type="button" className="btn btn--danger" onClick={() => cancel(detail)}>
                    {t('agenda.cancel')}
                  </button>
                  {/* A RDV placed by teleprospection is confirmed by the person
                      who will actually take it. */}
                  {detail.status === 'SCHEDULED' ? (
                    <button type="button" className="btn" onClick={() => changeStatus(detail, 'CONFIRMED')}>
                      {t('agenda.confirm')}
                    </button>
                  ) : null}
                  <button type="button" className="btn btn--primary" onClick={() => changeStatus(detail, 'DONE')}>
                    {t('agenda.status.DONE')}
                  </button>
                </>
              ) : null}
            </>
          ) : null
        }
      >
        {detail ? (
          <dl className="detail-list">
            <div>
              <dt>{t('common.date')}</dt>
              <dd>
                {formatDayLong(detail.startAt, i18n.resolvedLanguage)}{' '}
                {formatTime(detail.startAt, i18n.resolvedLanguage)} -{' '}
                {formatTime(detail.endAt, i18n.resolvedLanguage)}
              </dd>
            </div>
            <div>
              <dt>{t('customers.commercial')}</dt>
              <dd>{fullName(detail.commercial)}</dd>
            </div>
            <div>
              <dt>{t('agenda.bookedBy')}</dt>
              <dd>{detail.bookedBy ? fullName(detail.bookedBy) : '-'}</dd>
            </div>
            <div>
              <dt>{t('customers.title')}</dt>
              <dd>
                {detail.customerId ? (
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setDetail(null);
                      navigate(`/customers/${detail.customerId}`);
                    }}
                  >
                    {detail.customerName ?? t('customers.detail')}
                  </button>
                ) : (
                  detail.customerName ?? '-'
                )}
              </dd>
            </div>
            <div>
              <dt>{t('customers.phone')}</dt>
              <dd>{detail.customerPhone || '-'}</dd>
            </div>
            <div>
              <dt>{t('agenda.channel')}</dt>
              <dd>{t(`agenda.channels.${detail.channel}`)}</dd>
            </div>
            <div>
              <dt>{t('agenda.location')}</dt>
              <dd>{detail.location || '-'}</dd>
            </div>
            <div>
              <dt>{t('common.status')}</dt>
              <dd>
                <Select
                  value={detail.status}
                  onChange={(value) => changeStatus(detail, value)}
                  options={APPOINTMENT_STATUSES.map((status) => ({ value: status, label: t(`agenda.status.${status}`) }))}
                />
              </dd>
            </div>
            <div className="span-2">
              <dt>{t('common.notes')}</dt>
              <dd>{detail.notes || '-'}</dd>
            </div>
          </dl>
        ) : null}
      </Modal>
    </>
  );
};

export default CalendarPage;
